import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ApiFeil } from '../../api/klient';
import { konsollApi, type SammendragInfo } from '../../api/konsoll';
import type { Sammendrag } from '../../api/reise';
import SammendragModuler from '../reise/SammendragModuler';
import { datoTid } from './ReiseListe';

interface Props {
  reiseId: string;
  tilstand: string;
  onEndret: () => void;
}

/**
 * Den siste versjonen kunden har bekreftet, slik kunden så den. En ansatt godkjenner den, og den får status
 * «klar for bygging». Overleveringen bygges fra den (T081).
 */
export default function SammendragPanel({ reiseId, tilstand, onEndret }: Props) {
  const { t, i18n } = useTranslation();
  const [laast, setLaast] = useState<{ versjon: SammendragInfo; dokument: Sammendrag } | null | undefined>(undefined);
  const [feil, setFeil] = useState<string | null>(null);

  const hent = useCallback(
    () =>
      konsollApi
        .sammendrag(reiseId)
        .then(setLaast)
        .catch((e) => (e instanceof ApiFeil && e.kode === 'ingen_sammendrag' ? setLaast(null) : setFeil(e instanceof ApiFeil ? e.kode : 'ukjent'))),
    [reiseId]
  );

  useEffect(() => {
    hent();
  }, [hent]);

  async function godkjenn() {
    setFeil(null);
    try {
      await konsollApi.godkjenn(reiseId);
      await hent();
      onEndret();
    } catch (e) {
      setFeil(e instanceof ApiFeil ? e.kode : 'ukjent');
    }
  }

  if (laast === undefined && !feil) return <p role="status">{t('konsoll.laster')}</p>;
  if (laast === null) return <p className="text-slate-700">{t('konsoll.sammendrag.ingen')}</p>;

  const v = laast?.versjon;
  return (
    <div>
      {feil && (
        <p role="alert" className="mb-4 melding-feil">
          {t(`feil.${feil}`, { defaultValue: t('feil.ukjent') })}
        </p>
      )}
      {v && laast && (
        <>
          <p className="mb-3 font-medium">{t('konsoll.sammendrag.versjon', { versjon: v.versjon, dato: datoTid(v.bekreftet, i18n.resolvedLanguage), av: v.bekreftetAv })}</p>

          {v.godkjent ? (
            <p role="status" className="mb-4 flex items-center gap-2 melding-ok">
              <CheckCircle2 aria-hidden="true" className="h-5 w-5 shrink-0" />
              {t('konsoll.sammendrag.godkjent', { dato: datoTid(v.godkjent, i18n.resolvedLanguage), av: v.godkjentAv })}
            </p>
          ) : tilstand === 'bekreftet' ? (
            <div className="mb-4 kort p-5">
              <p className="mb-3 text-slate-700">{t('konsoll.sammendrag.godkjennTekst')}</p>
              <button type="button" onClick={godkjenn} className="knapp-primaer">
                {t('konsoll.sammendrag.godkjenn')}
              </button>
            </div>
          ) : (
            <p className="mb-4 melding-advarsel">{t('konsoll.sammendrag.aapnetIgjen')}</p>
          )}

          <SammendragModuler dokument={laast.dokument} />
          <p className="mt-4 text-xs text-slate-500">{t('reise.sammendrag.innholdsversjon', { versjon: laast.dokument.innholdVersjon, fingeravtrykk: v.fingeravtrykk.slice(0, 12) })}</p>
        </>
      )}
    </div>
  );
}
